// @flow
import React from 'react';
import { Modal, Form, Input, Button, TimePicker, Space, Divider } from 'antd';
import moment from 'moment';
import '../../Modal.scss';

export default function AttendanceEditModal({
  visible,
  onCancel,
  onSave,
  selectedDayData,
  currentUser,
}: {
  visible: boolean,
  onCancel: () => void,
  onSave: (data: any) => void,
  selectedDayData: any,
  currentUser: any,
}) {
  const [form] = Form.useForm();
  const [programs, setPrograms] = React.useState([]);

  // Initialize form when modal opens with selected day data
  React.useEffect(() => {
    if (visible && selectedDayData) {
      const initialPrograms = selectedDayData.programs.map(
        (program, index) => ({
          id: index,
          program: program.program || '',
          hours: program.hours || '00:00',
        })
      );
      setPrograms(initialPrograms);

      // Set form values
      const formValues = {};
      initialPrograms.forEach((program, index) => {
        formValues[`program_${program.id}`] = program.program;
        formValues[`hours_${program.id}`] = moment(program.hours, 'HH:mm');
      });
      form.setFieldsValue(formValues);
    }
  }, [visible, selectedDayData, form]);

  // Handle form submission
  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const updatedPrograms = programs
          .map((program) => ({
            program: values[`program_${program.id}`] || '',
            hours: values[`hours_${program.id}`]
              ? values[`hours_${program.id}`].format('HH:mm')
              : '00:00',
          }))
          .filter((p) => p.program.trim() !== ''); // Remove empty programs (but allow 00:00 hours)

        if (updatedPrograms.length === 0) {
          // TODO: Show error message - at least one program required
          return;
        }

        const saveData = {
          participant: selectedDayData.participant,
          date: selectedDayData.date,
          programs: updatedPrograms,
          totalHours: calculateTotalHours(updatedPrograms),
        };

        // TODO: Add audit logging for attendance save attempts
        // Should log: user, participant, date, timestamp, action: 'EDIT_ATTENDANCE_SAVED', changes: before/after

        onSave(saveData);
      })
      .catch((errorInfo) => {
        // TODO: Add user-friendly form validation error handling
        // Could highlight invalid fields or show validation messages
      });
  };

  // Calculate total hours from all programs
  const calculateTotalHours = (programList) => {
    let totalMinutes = 0;
    programList.forEach((program) => {
      const [hours, minutes] = program.hours.split(':').map(Number);
      totalMinutes += hours * 60 + minutes;
    });

    const totalHours = Math.floor(totalMinutes / 60);
    const remainingMinutes = totalMinutes % 60;
    return `${totalHours
      .toString()
      .padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}`;
  };

  // Format time for display
  const formatTimeDisplay = (hours) => {
    if (!hours || hours === '00:00') return '0m';
    const [h, m] = hours.split(':').map(Number);
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  // Format the date for display
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return '';

    // Parse the full ServiceDate (format: "YYYY/MM/DD" e.g., "2025/09/01")
    const date = moment(dateStr, 'YYYY/MM/DD');

    return date.format('MMMM Do, YYYY'); // e.g., "September 1st, 2025"
  };

  if (!selectedDayData) return null;

  return (
    <Modal
      title={`Edit Attendance - ${
        selectedDayData.participant
      } - ${formatDateForDisplay(selectedDayData.date)}`}
      visible={visible}
      onCancel={onCancel}
      width={600}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSubmit}>
          Save Changes
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <div style={{ marginBottom: '16px', color: '#666' }}>
          <strong>Date:</strong> {formatDateForDisplay(selectedDayData.date)} |{' '}
          <strong>Current Total:</strong>{' '}
          {formatTimeDisplay(selectedDayData.totalHours)}
        </div>

        <Divider>Programs</Divider>

        {programs.map((program, index) => (
          <div
            key={program.id}
            style={{
              marginBottom: '16px',
              padding: '12px',
              border: '1px solid #f0f0f0',
              borderRadius: '4px',
            }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span style={{ fontWeight: 'bold' }}>Program {index + 1}</span>
              </div>

              <Form.Item
                name={`program_${program.id}`}
                label="Program Name"
                rules={[
                  { required: true, message: 'Program name is required' },
                ]}
                style={{ marginBottom: '8px' }}
              >
                <Input placeholder="Enter program name" />
              </Form.Item>

              <Form.Item
                name={`hours_${program.id}`}
                label="Hours"
                rules={[
                  { required: true, message: 'Hours are required' },
                  {
                    validator: (_, value) => {
                      // Allow any time value including 00:00
                      if (!value) {
                        return Promise.reject(new Error('Please select hours'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                style={{ marginBottom: '0' }}
              >
                <TimePicker
                  format="HH:mm"
                  placeholder="Select hours"
                  showNow={false}
                  allowClear={false}
                  style={{ width: '120px' }}
                />
              </Form.Item>
            </Space>
          </div>
        ))}

        {/*
        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addProgram}
          style={{ width: '100%', marginTop: '8px' }}
        >
          Add Another Program
        </Button>
        */}
      </Form>
    </Modal>
  );
}
